import { computed, Injectable, Signal, signal } from '@angular/core';

import { AppConfig, GlobalThis, RuntimeEndpoints } from './app.config.types';

// read reactively by computed() signals inside the service constructor.
const runtimeEndpoints = signal<RuntimeEndpoints | null>(null);

/** Reset the runtime endpoints signal. For unit tests only. */
export function resetRuntimeEndpointsForTesting(): void {
  runtimeEndpoints.set(null);
}

/** Directly set the runtime endpoints signal. For unit tests only. */
export function setRuntimeEndpointsForTesting(
  endpoints: RuntimeEndpoints | null,
): void {
  runtimeEndpoints.set(endpoints);
}

@Injectable({
  providedIn: 'root',
})
export class AppConfigService {
  private readonly config: AppConfig;

  /** Base URL for admin-service calls. */
  readonly adminServiceUrl: Signal<string>;
  /** Base URL for proby-service calls. */
  readonly probyServiceUrl: Signal<string>;

  constructor() {
    const appGlobal = globalThis as typeof globalThis & {
      appConfig?: AppConfig;
    };
    this.config = appGlobal.appConfig as AppConfig;
    this.adminServiceUrl = computed(
      () => runtimeEndpoints()?.adminService ?? '',
    );
    this.probyServiceUrl = computed(
      () => runtimeEndpoints()?.probyService ?? '',
    );
  }

  /**
   * Loads app config first, then fetches runtime endpoints using the PWA URL from config.
   * Returns false if endpoints could not be loaded (caller should redirect to 503).
   * Skips endpoint fetch when on the service-unavailable page to prevent redirect loops.
   */
  static async initialize(serviceUnavailablePath: string): Promise<boolean> {
    const isServiceUnavailablePage = globalThis.location.pathname.includes(
      serviceUnavailablePath,
    );

    let config: AppConfig;

    try {
      config = await AppConfigService.loadAppConfig();
    } catch (err) {
      console.error('Error during initialization:', err);
      return false;
    }

    if (isServiceUnavailablePage) {
      return true;
    }

    return await AppConfigService.loadRuntimeEndpoints(config.pwaUrl);
  }

  /** Fetches AppConfig from the server before Angular bootstraps. */
  static async loadAppConfig(): Promise<AppConfig> {
    const url = '/api/config';
    const response = await fetch(url, { method: 'GET' });

    if (!response.ok) {
      throw new Error(`Failed to load app config: ${response.status}`);
    }

    let config: AppConfig;
    try {
      config = (await response.json()) as AppConfig;
    } catch (err) {
      console.error('Error parsing app config:', err);
      throw err;
    }

    (globalThis as GlobalThis).appConfig = config;

    return config;
  }

  /**
   * Fetches runtime service endpoints directly from the PWA and stores them in a
   * module-level signal before the Angular app bootstraps. Called as a static method
   * so it requires no DI context. The PWA URL is sourced from the app config loaded
   * by {@link loadAppConfig}.
   */
  static async loadRuntimeEndpoints(pwaUrl: string): Promise<boolean> {
    const url = `${pwaUrl}/api/endpoints`;
    try {
      const endpointsResponse = await fetch(url, { method: 'GET' });
      if (!endpointsResponse.ok) {
        throw new Error(
          `Failed to fetch runtime endpoints: ${endpointsResponse.status}`,
        );
      }
      runtimeEndpoints.set(
        (await endpointsResponse.json()) as RuntimeEndpoints,
      );
      return true;
    } catch (runtimeErr) {
      console.warn(
        'Failed to load runtime endpoints. Falling back to config.json URL.',
        runtimeErr,
      );
      return false;
    }
  }

  get keys(): AppConfig {
    return this.config;
  }
}
